/**
 * Progressive lip-sync helpers (Companion SDK 2.12.0, 5 Sep 2026).
 *
 * Background — /tmp/avatar-sync/REPORT.md, Keith 5 Sep 20:26 ("the speech
 * starts a few seconds before the avatar starts talking"). Measured on the
 * three dev sites: audio-playing -> first mouth movement was a median 3.6-6 s
 * for replies and 3.7-7 s for the greeting. Two different causes:
 *
 *   1. Replies: the runtime streams `visual.blendshapeChunk` in real time
 *      (chunk 0 lands 0.2-1.2 s BEFORE the /talk/stream MP3 starts), but
 *      `LiveModeClient` handed the renderer the frames only on the isFinal
 *      chunk — after Azure had synthesised the whole 60-75 s reply (4-12 s).
 *      -> three-stage progressive dispatch (browser-only chat path only):
 *         stage 1  chunk 0 as soon as the <audio> element is playing;
 *         stage 2  one superset re-dispatch once >= 8 s of frames are buffered;
 *         stage 3  the final superset, flushed at the next speech PAUSE after
 *                  the audio position (or when the running clip is about to
 *                  run dry) so the O(frames) clip rebuild in Avatar.tsx never
 *                  freezes a moving mouth.
 *      Frames are absolute-timed and the renderer anchors every dispatch to
 *      `<audio>.currentTime` (Avatar.tsx "no catch-up jump"), so each superset
 *      is a seamless upgrade of the running animation.
 *
 *   2. Greeting: the 3D model is still instantiating (Draco decode, morph
 *      meshes, shader links — a 2-8 s main-thread stall) when the greeting
 *      audio starts ~1 s after the consent click.
 *      -> hold the greeting's <audio>.play() until `avatar:model:ready`
 *         (bounded: a model failure must never mute the greeting).
 *
 * Everything here is pure so it can be unit-tested without React.
 */

export interface ProgressiveLipSyncFrame {
  time: number;
  /** ARKit-style blendshape weights; absent/empty for a silent frame. */
  values?: Readonly<Record<string, number>>;
}

/** Feature flag: `avatar.lipSync.progressive` (Companion) / `setProgressiveLipSyncEnabled`. */
let progressiveLipSyncEnabled = true;

/**
 * Default ON. Only the browser-only chat path consults it — CamlinVoice-driven
 * surfaces (Visual IVR, browser WebRTC live) keep the whole-utterance dispatch
 * because their audio waits for `visual.animation.ready`.
 */
export function setProgressiveLipSyncEnabled(enabled: boolean): void {
  progressiveLipSyncEnabled = enabled !== false;
}

export function isProgressiveLipSyncEnabled(): boolean {
  return progressiveLipSyncEnabled;
}

/** Feature flag: `avatar.lipSync.greetingWaitsForModel` (Companion). Default ON. */
let greetingModelReadyGateEnabled = true;

export function setGreetingModelReadyGateEnabled(enabled: boolean): void {
  greetingModelReadyGateEnabled = enabled !== false;
}

export function isGreetingModelReadyGateEnabled(): boolean {
  return greetingModelReadyGateEnabled;
}

/**
 * Stage-2 trigger: re-dispatch the superset once the buffer covers this much
 * speech. Azure synthesises at ~15-18x real time and chunk 0 lands 0.2-1.2 s
 * before the MP3 starts, so the buffer normally holds 8 s of frames BEFORE
 * the <audio> element plays — the first flush then already carries 8+ s and
 * the final superset (which arrives ~4 s later, audio at ~3 s) can wait for a
 * pause instead of a run-dry flush. If Azure is slow the stage-2 superset
 * lands mid-speech instead; its rebuild is ~500 frames (tens of ms).
 */
export const PROGRESSIVE_STAGE2_MIN_BUFFERED_MS = 8000;

/**
 * A speech pause is at least this long — either a gap between consecutive
 * frame times, or a run of frames whose mouth is closed (Azure emits a
 * continuous 60 fps timeline with near-zero weights through silences, so the
 * value scan is the one that fires on real replies; the gap scan covers
 * sparse timelines).
 */
export const PROGRESSIVE_PAUSE_GAP_MS = 250;

/**
 * "Closed mouth" is RELATIVE to the clip's own resting jaw: Azure's neutral
 * pose is not zero (2.12.1 field capture on companion-dev: jawOpen never
 * below 0.139 in a 9 s greeting, p05 0.155, speech median 0.37), so an
 * absolute threshold near 0 never fires. Threshold = 5th-percentile jawOpen
 * + this margin. With sparse frames (no weights through silence) the
 * baseline is 0 and the margin alone applies.
 */
export const PROGRESSIVE_PAUSE_JAW_MARGIN = 0.06;

/** Quantile of jawOpen used as the clip's resting-jaw baseline. */
export const PROGRESSIVE_PAUSE_BASELINE_QUANTILE = 0.05;

/** Pause boundaries are reported this far inside the silence. */
export const PROGRESSIVE_PAUSE_ENTRY_MS = 60;

function jawWeight(frame: ProgressiveLipSyncFrame): number {
  const weight = frame.values?.jawOpen;
  return typeof weight === 'number' && Number.isFinite(weight) ? Math.abs(weight) : 0;
}

/**
 * Flush the final superset when the running clip has this little left, even
 * if no pause has come along — the mouth must never run dry mid-sentence.
 */
export const PROGRESSIVE_RUN_DRY_LEAD_MS = 2000;

/** Never hold the final superset longer than this after it arrived. */
export const PROGRESSIVE_FINAL_MAX_HOLD_MS = 6000;

/**
 * A clip whose jaw barely moves (p95 - p05 below this) has no usable
 * speech/silence contrast — the value scan is skipped for it rather than
 * declaring the whole clip a pause.
 */
export const PROGRESSIVE_PAUSE_MIN_JAW_RANGE = 0.1;

/**
 * Resting-jaw threshold for a frame set: 5th-percentile jawOpen + margin, or
 * null when the clip has no speech/silence contrast (exported for tests and
 * the flush diagnostics).
 */
export function pauseJawThreshold(
  frames: readonly ProgressiveLipSyncFrame[],
  margin: number = PROGRESSIVE_PAUSE_JAW_MARGIN,
  quantile: number = PROGRESSIVE_PAUSE_BASELINE_QUANTILE
): number | null {
  if (frames.length === 0) return null;
  const sorted = frames.map(jawWeight).sort((left, right) => left - right);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * (sorted.length - 1)))];
  const baseline = at(quantile);
  if (at(1 - quantile) - baseline < PROGRESSIVE_PAUSE_MIN_JAW_RANGE) return null;
  return baseline + margin;
}

/**
 * Pause boundaries (ms, clip time) inside a frame timeline, sorted and
 * de-duplicated. Each is PROGRESSIVE_PAUSE_ENTRY_MS inside a silence that
 * lasts >= gapMs, so a flush there lands while the mouth is at rest and the
 * clip rebuild finishes before speech resumes. Two scans:
 *
 *   - a gap >= gapMs between consecutive frame times (sparse timelines), and
 *   - a run >= gapMs of consecutive frames whose jawOpen is below the clip's
 *     resting-jaw threshold (`pauseJawThreshold`; Azure's continuous 60 fps
 *     timelines — the one that fires on real replies). Skipped (null
 *     threshold) when the clip has no speech/silence contrast.
 *
 * Hold frames inserted by `insertHoldFramesForPauses` sit 16 ms inside a gap
 * and carry the last spoken weights, so they never hide a pause from either
 * scan.
 */
export function computePauseTimesMs(
  frames: readonly ProgressiveLipSyncFrame[],
  gapMs: number = PROGRESSIVE_PAUSE_GAP_MS,
  jawThreshold: number | null = pauseJawThreshold(frames)
): number[] {
  const sorted = frames
    .filter((frame) => Number.isFinite(frame.time))
    .slice()
    .sort((left, right) => left.time - right.time);
  const pauses = new Set<number>();
  let closedSinceMs: number | null = null;
  let closedReported = false;
  for (let index = 0; index < sorted.length; index += 1) {
    const frame = sorted[index];
    const timeMs = frame.time * 1000;
    if (index > 0) {
      const previousMs = sorted[index - 1].time * 1000;
      if (timeMs - previousMs >= gapMs) {
        pauses.add(Math.round(previousMs + PROGRESSIVE_PAUSE_ENTRY_MS));
      }
    }
    if (jawThreshold !== null && jawWeight(frame) < jawThreshold) {
      if (closedSinceMs === null) {
        closedSinceMs = timeMs;
        closedReported = false;
      } else if (!closedReported && timeMs - closedSinceMs >= gapMs) {
        pauses.add(Math.round(closedSinceMs + PROGRESSIVE_PAUSE_ENTRY_MS));
        closedReported = true;
      }
    } else {
      closedSinceMs = null;
      closedReported = false;
    }
  }
  return Array.from(pauses).sort((left, right) => left - right);
}

export interface FinalFlushDecisionInput {
  /** Current <audio>.currentTime in ms. */
  audioPositionMs: number;
  /** Pause boundaries of the FINAL superset (clip ms), from computePauseTimesMs. */
  pauseTimesMs: readonly number[];
  /** Last frame time (clip ms) of the clip the renderer is currently running. */
  dispatchedClipEndMs: number;
  /** ms elapsed since the final superset arrived. */
  heldForMs: number;
  /** Lead time so the rebuild lands inside the pause rather than after it. */
  leadMs?: number;
}

export type FinalFlushReason = 'pause' | 'run-dry' | 'max-hold' | null;

/**
 * Decide whether the pause-scheduled final superset should flush NOW.
 *
 *  - 'pause'    the audio position (+lead) has reached a pause boundary that is
 *               still ahead of (or at) the position — mouth is closed anyway.
 *  - 'run-dry'  the currently dispatched clip ends within RUN_DRY_LEAD_MS.
 *  - 'max-hold' the superset has been held too long (no pause in a long
 *               run-on sentence) — accept one visible rebuild.
 */
export function decideFinalFlush({
  audioPositionMs,
  pauseTimesMs,
  dispatchedClipEndMs,
  heldForMs,
  leadMs = 40
}: FinalFlushDecisionInput): FinalFlushReason {
  if (dispatchedClipEndMs - audioPositionMs <= PROGRESSIVE_RUN_DRY_LEAD_MS) {
    return 'run-dry';
  }
  if (heldForMs >= PROGRESSIVE_FINAL_MAX_HOLD_MS) {
    return 'max-hold';
  }
  const horizon = audioPositionMs + leadMs;
  for (const pause of pauseTimesMs) {
    // A pause we are inside of or just about to enter. Pauses already well
    // behind the position are ignored (we missed them).
    if (pause <= horizon && pause >= audioPositionMs - leadMs) {
      return 'pause';
    }
  }
  return null;
}

/** Last frame time of a frame set in ms (0 for an empty set). */
export function clipEndMs(frames: readonly ProgressiveLipSyncFrame[]): number {
  let end = 0;
  for (const frame of frames) {
    if (Number.isFinite(frame.time) && frame.time > end) end = frame.time;
  }
  return Math.round(end * 1000);
}

// ---------------------------------------------------------------------------
// Greeting gate — `avatar:model:ready`
// ---------------------------------------------------------------------------

/**
 * Bounded wait: a model that never loads must not mute the greeting.
 *
 * 8 s, not the 4 s first proposed: on companion-dev the greeting prompt
 * arrives ~1 s after the consent click while a cold model load completes
 * 5-7 s later at 1x CPU (2.12.0 run 3: timed out at 4 s, model ready 2.6 s
 * after the audio started — the residual gap the gate exists to remove).
 * A genuine model failure still un-mutes after 8 s with the greeting text
 * already on screen.
 */
export const MODEL_READY_GATE_TIMEOUT_MS = 8000;

let modelReadyObserved = false;
let modelReadyListenerInstalled = false;

/**
 * Installed once per page (module load in the browser). Records whether the
 * renderer has EVER reported `avatar:model:ready`, so the gate only ever
 * holds the first utterance after a cold mount — hosts whose avatar was
 * already loaded before the live channel came up are never delayed.
 */
export function installModelReadyObserver(target: Pick<Window, 'addEventListener'> | undefined = typeof window !== 'undefined' ? window : undefined): void {
  if (modelReadyListenerInstalled || !target) return;
  modelReadyListenerInstalled = true;
  target.addEventListener('avatar:model:ready', () => {
    modelReadyObserved = true;
  });
}

export function hasModelReadyBeenObserved(): boolean {
  return modelReadyObserved;
}

/** Test seam. */
export function resetModelReadyObserverForTests(): void {
  modelReadyObserved = false;
  modelReadyListenerInstalled = false;
}

export interface WaitForModelReadyOptions {
  timeoutMs?: number;
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  setTimeoutFn?: (fn: () => void, ms: number) => number;
  clearTimeoutFn?: (id: number) => void;
}

export type ModelReadyWaitResult = 'already-ready' | 'ready' | 'timeout' | 'cancelled';

/**
 * Resolve as soon as the model is ready; never later than `timeoutMs`.
 * Returns a handle so a Stop / new prompt can cancel the wait.
 */
export function waitForModelReady({
  timeoutMs = MODEL_READY_GATE_TIMEOUT_MS,
  target = typeof window !== 'undefined' ? window : undefined,
  setTimeoutFn = (fn, ms) => window.setTimeout(fn, ms),
  clearTimeoutFn = (id) => window.clearTimeout(id)
}: WaitForModelReadyOptions = {}): { promise: Promise<ModelReadyWaitResult>; cancel: () => void } {
  if (modelReadyObserved || !target) {
    return { promise: Promise.resolve('already-ready'), cancel: () => {} };
  }
  let settle: (result: ModelReadyWaitResult) => void = () => {};
  let timer: number | null = null;
  const onReady = () => finish('ready');
  const finish = (result: ModelReadyWaitResult) => {
    target.removeEventListener('avatar:model:ready', onReady);
    if (timer !== null) {
      clearTimeoutFn(timer);
      timer = null;
    }
    settle(result);
  };
  const promise = new Promise<ModelReadyWaitResult>((resolve) => {
    settle = resolve;
    target.addEventListener('avatar:model:ready', onReady);
    timer = setTimeoutFn(() => finish('timeout'), timeoutMs);
  });
  return { promise, cancel: () => finish('cancelled') };
}

installModelReadyObserver();
